import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Check, Info } from 'lucide-react';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { useAuth } from '@/contexts/AuthContext';
import { TimezoneNote } from '@/lib/timezone';

/**
 * « Demande envoyée ! » — shown once the server has created the booking
 * request (scooters, transfers, private drivers, excursions): its reference,
 * the provider still has to accept, nothing is paid yet, link to the
 * traveller's bookings. « Continuer » goes to `continuePath`.
 */
export const BookingRequestSentDialog = ({ open, onOpenChange, bookingId, continuePath }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { user } = useAuth();

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="confirmation-dialog">
        <DialogHeader>
          <DialogTitle className="text-center text-2xl">
            <div className="mx-auto mb-3 w-16 h-16 rounded-full bg-accent/10 flex items-center justify-center">
              <Check className="w-8 h-8 text-accent" strokeWidth={3} />
            </div>
            {t('scooters.detail.confirmation.title')}
          </DialogTitle>
          <DialogDescription className="text-center text-base pt-2">
            {t('scooters.detail.confirmation.subtitle')}
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3 text-sm text-ink-light text-center mt-2">
          {bookingId && (
            <p className="text-xs font-mono bg-accent/10 text-accent rounded-lg p-2" data-testid="created-booking-id">
              {t('scooters.detail.confirmation.reference', { id: bookingId })}
            </p>
          )}
          <p>{t('scooters.detail.confirmation.nextStepPlain')}</p>
          <p className="text-xs bg-background-soft rounded-lg p-3">
            <Info className="w-4 h-4 text-accent inline-block mr-1 mb-0.5" />
            {t('scooters.detail.confirmation.noPayment')}
          </p>
          <TimezoneNote className="text-center" />
        </div>
        <div className="mt-4 flex gap-2">
          <button
            onClick={() => { onOpenChange(false); navigate(continuePath); }}
            className="btn-outline flex-1"
            data-testid="continue-browsing"
          >
            {t('scooters.detail.confirmation.continue')}
          </button>
          {user && (
            <button
              onClick={() => { onOpenChange(false); navigate(user?.role === 'provider' ? '/dashboard?tab=trips' : '/dashboard'); }}
              className="btn-primary flex-1"
              data-testid="go-to-dashboard"
            >
              {t('scooters.detail.confirmation.myBookings')}
            </button>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default BookingRequestSentDialog;
